import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { Link2, Send } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { CardRadio } from '@/components/ui/CardRadio'
import { DialogFooter } from '@/components/ui/dialog'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { CacheKeys } from '@/lib/cache'
import { copyText } from '@/lib/clipboard'
import type { NewTeammate } from '@/models/confirmInfo'
import {
  type ConfirmLinkTarget,
  createConfirmLink,
  type CustomerLink,
  emailConfirmLink,
  fillApplicationErrorMessage,
  useConfirmLinkRecipients,
} from '@/models/fillApplication'
import { useCurrentUser } from '@/models/user'
import { isPersonFilled, NewPersonFields, NO_PERSON } from '../../ConfirmInfo/NewPersonFields'
import { LinkField } from './FillApplication/LinkField'

const SOMEONE_NEW = 'new'
const SUBJECT = 'Your insurance application'

/** A note from the rep, not a system email: from their own mailbox when it's connected, else under their name. */
function defaultMessage(firstName: string, staffName: string): string {
  return [
    `Hi ${firstName || 'there'},`,
    "I filled in your insurance application with what we have so far. Can you check it, add anything that's missing, and confirm it's accurate? Once you do, I'll get your quotes from the carriers.",
    '{link}',
    `Thanks,\n${staffName}`,
  ].join('\n\n')
}

interface ConfirmLinkFormProps {
  orgId: string
  onSent: () => void
}

export function ConfirmLinkForm({ orgId, onSent }: ConfirmLinkFormProps) {
  const queryClient = useQueryClient()
  const { user } = useCurrentUser({ enabled: false })
  const { recipients, suggested, isLoading } = useConfirmLinkRecipients(orgId, { enabled: true })
  const [pick, setPick] = useState<string | null>(null)
  const [typedPerson, setPerson] = useState<NewTeammate | null>(null)
  const [subject, setSubject] = useState(SUBJECT)
  const [message, setMessage] = useState<string | null>(null)
  const [link, setLink] = useState<CustomerLink | null>(null)
  const [busy, setBusy] = useState<'copy' | 'send' | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Someone new starts as whoever sent the large-fleet packet, until the rep types over it.
  const person = typedPerson ?? suggested ?? NO_PERSON
  const chosen = pick ?? recipients.at(0)?.id ?? SOMEONE_NEW
  const recipient = recipients.find((person) => person.id === chosen)
  const firstName = recipient ? recipient.name.split(' ')[0] : person.firstName.trim()
  // Rewritten for whoever is picked until the rep edits it.
  const body = message ?? defaultMessage(firstName, user?.firstName ?? '')
  const target: ConfirmLinkTarget | null = recipient
    ? { userId: recipient.id }
    : isPersonFilled(person)
      ? { firstName: person.firstName.trim(), lastName: person.lastName.trim(), email: person.email.trim() }
      : null

  // A link signs in as the person it was made for, so it goes when they change.
  function choose(id: string) {
    setPick(id)
    setLink(null)
  }

  function changePerson(next: NewTeammate) {
    setPerson(next)
    setLink(null)
  }

  async function act(kind: 'copy' | 'send') {
    if (!target) return
    // Acting adds the suggested person as a user, which drops the suggestion; keep their fields.
    if (!recipient) setPerson(person)
    setBusy(kind)
    setError(null)
    try {
      if (kind === 'copy') {
        const made = await createConfirmLink(orgId, target)
        setLink(made)
        await copyText(made.url).then(
          () => toast.success('Link copied'),
          () => undefined
        )
      } else {
        await emailConfirmLink(orgId, target, { subject: subject.trim(), message: body })
        toast.success('Confirm link sent')
        onSent()
      }
    } catch (failure) {
      setError(fillApplicationErrorMessage(failure))
    } finally {
      setBusy(null)
      // Someone new is now one of the customer's users, here and on Overview.
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId] })
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.FILL_APPLICATION_STATE, orgId] })
    }
  }

  if (isLoading) return <SkeletonRows rows={3} />

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">Send it to</span>
        {recipients.map((option) => (
          <CardRadio key={option.id} isSelected={chosen === option.id} onSelect={() => choose(option.id)}>
            {option.name ? `${option.name} · ${option.email}` : option.email}
          </CardRadio>
        ))}
        <CardRadio isSelected={chosen === SOMEONE_NEW} onSelect={() => choose(SOMEONE_NEW)}>
          Someone new
        </CardRadio>
        {chosen === SOMEONE_NEW && (
          <NewPersonFields idPrefix="confirm-link" person={person} onChange={changePerson} />
        )}
      </div>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="confirm-link-subject">Subject</FieldLabel>
          <Input
            id="confirm-link-subject"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="confirm-link-message">Message</FieldLabel>
          <Textarea
            id="confirm-link-message"
            rows={9}
            value={body}
            onChange={(event) => setMessage(event.target.value)}
          />
          <span className="text-ink-500 text-xs">The link goes where {'{link}'} is.</span>
        </Field>
      </FieldGroup>
      {link && (
        <div className="flex flex-col gap-2">
          <LinkField url={link.url} label="Confirm link" />
          <span className="text-ink-500 text-xs">
            Signs in as {link.user.name} ({link.user.email}) until {format(new Date(link.expiresAt), 'MMM d')}
            . Anyone with the link can use it, so don&apos;t open it yourself.
          </span>
        </div>
      )}
      <FieldError className="text-xs">{error}</FieldError>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => void act('copy')}
          disabled={!target || busy !== null}
          loading={busy === 'copy' ? 'Making the link…' : undefined}
        >
          <Link2 /> Copy link
        </Button>
        <Button
          type="button"
          onClick={() => void act('send')}
          disabled={!target || !subject.trim() || busy !== null}
          loading={busy === 'send' ? 'Sending…' : undefined}
        >
          <Send /> Send email
        </Button>
      </DialogFooter>
    </div>
  )
}
